import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Modal, { ModalProps } from '../Modal';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { H2, P } from '@osmosis/tailwind-ui/components/Text';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import {
  text5,
  textRed,
  text3,
  textDarkBlue,
} from '@osmosis/tailwind-ui/styles/text';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import Image from 'next/image';
import image from '../../assets/mo-sad.png';
import { BreadcrumbV2Object } from '@osmosis/shared/types/learn';
import { useLearnData } from '../../context/LearnDataProvider';
import { IconButton } from '@osmosis/tailwind-ui/components/IconButton';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';
import {
  ExpiredUserModalType,
  getExpiredUserModalConfig,
  expiredUserModalMessages,
} from '../../constants/ExpiredUserModalConfig';
import { track } from '../../lib/util/tracking';
import { useUserContext } from '@osmosis/nextjs-components/context/UserContext';

/**
 * Formats text by replacing <blue>text</blue> tags with blue-colored spans.
 * @param text - The text that may contain <blue> tags.
 * @returns Array of React elements with styled text.
 */
export const formatTextWithHighlights = (text: string): React.ReactNode[] => {
  if (!text) return [text];

  // Split the text by the opening and closing tags
  const parts = text.split(/<blue>|<\/blue>/);
  const result: React.ReactNode[] = [];

  parts.forEach((part, index) => {
    if (index % 2 === 1) {
      const contentKey = `blue-${part
        .trim()
        .substring(0, 4)
        .replace(/\s+/g, '-')}`;
      result.push(
        <span key={contentKey} className={cn(textDarkBlue)}>
          {part}
        </span>,
      );
    } else {
      result.push(part);
    }
  });

  return result;
};

const getLibraryUrl = (breadcrumbs: BreadcrumbV2Object[] = []) => {
  const link = breadcrumbs?.[0]?.link || '';
  return `/library${link}`;
};

const CloseButton = ({ hide }: { hide: () => void }) => (
  <Box
    data-testid="close_button"
    className="absolute right-4 top-4 z-10 flex items-center justify-center"
  >
    <IconButton
      data-testid="close_button_icon"
      variant="transparent"
      icon={<Icon icon={faTimes} />}
      className={cn('text-lg', text3)}
      onClick={hide}
    />
  </Box>
);

type ExpiredUserLockoutModalProps = Omit<
  ModalProps,
  'heading' | 'subheading' | 'text' | 'data-testid'
> & {
  modalType: ExpiredUserModalType;
  preventClose?: boolean;
};

/**
 * A reusable modal that informs users they need Prime to access certain features.
 */

// ... existing imports

/**
 * A reusable modal that informs users they need Prime to access certain features.
 */
const ExpiredUserLockoutModal: React.FC<ExpiredUserLockoutModalProps> = ({
  modalType = ExpiredUserModalType.GENERAL,
  preventClose = false,
  ...modalProps
}) => {
  const intl = useIntl();
  const { learnData } = useLearnData();
  const libraryUrl = getLibraryUrl(learnData?.breadcrumbsv2);
  const hide = modalProps.hide;
  const modalConfig = getExpiredUserModalConfig(modalType, intl);
  const { getPlansUrl } = useUserContext();

  const expiredMessage = intl.formatMessage(
    expiredUserModalMessages.expiredMessage,
  );
  const topicsButtonEnabledTypes = [
    ExpiredUserModalType.VIDEO,
    ExpiredUserModalType.PLAYLIST,
    ExpiredUserModalType.LEARN_QUIZ,
  ];
  const shouldShowTopicsButton = topicsButtonEnabledTypes.includes(modalType);

  // Gated on isVisible: several callers mount this component while hidden.
  useEffect(() => {
    if (!modalProps.isVisible) return;
    track(
      'expired_modal_impression',
      {
        modalType,
        learnNodeID: learnData?.learnNodeID,
      },
      ['keen', 'pendo', 'gtm'],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalProps.isVisible]);

  // Track conversion actions with richer context.
  // The modal renders in a portal, but React synthetic clicks still bubble through the
  // React tree to ancestor handlers (e.g. the Videos-tab row's wrapper <button>) that
  // call preventDefault, which would cancel this anchor's navigation. Stop propagation
  // so the CTA reliably navigates from every lockout entry point.
  const handleViewPlansClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    track(
      'expired_modal_cta_click',
      {
        modalType: modalType,
        ctaType: 'view_plans',
      },
      ['keen', 'pendo', 'gtm'],
    );
  };

  const handleViewTopicsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    track(
      'expired_modal_cta_click',
      {
        modalType: modalType,
        ctaType: 'view_topics',
        learnNodeID: learnData?.learnNodeID || 0,
      },
      ['keen', 'pendo', 'gtm'],
    );
  };

  const handleModalClose = () => {
    track(
      'expired_modal_close',
      {
        modalType: modalType,
        learnNodeID: learnData?.learnNodeID || 0,
      },
      ['keen', 'pendo', 'gtm'],
    );
    hide();
  };

  return (
    // Focus trapping and initial focus are handled internally by the shared Modal component.
    // eslint-disable-next-line @nx/workspace/a11y/forbid-missing-initial-focus, @nx/workspace/a11y/require-focus-lock
    <Modal
      {...modalProps}
      className="text-center"
      _className="!max-w-2xl !w-full"
      hide={handleModalClose}
    >
      <Box
        className="min-h-128 relative flex flex-col pt-6"
        data-testid={modalConfig.testId}
      >
        <CloseButton hide={handleModalClose} />
        <Box className="mx-auto flex max-w-md flex-1 flex-col items-center justify-center">
          {modalConfig.icon ? (
            <Box className="mx-auto mb-4">
              <Image
                src={modalConfig.icon}
                alt={modalType.toLowerCase()}
                width={171}
                height={96}
                className="mx-auto"
              />
            </Box>
          ) : (
            <Image
              src={image}
              alt="Expired"
              width={171}
              height={96}
              className="mx-auto mb-4"
            />
          )}
          <H2 className={cn('mb-2', text5)}>{modalConfig.heading}</H2>
          <Box className="max-w-xs">
            <P className={cn('mb-4 font-bold', textRed)}>{expiredMessage}</P>
            <P className={cn('!text-md mb-8 block font-bold', text3)}>
              {formatTextWithHighlights(modalConfig.text)}
            </P>
          </Box>
          <Flex className="mt-2 justify-center space-x-4">
            <Button
              as="a"
              href={getPlansUrl()}
              color="blue"
              rounded={true}
              data-testid={`${modalConfig.testId}-cta-button`}
              onClick={handleViewPlansClick}
            >
              <FormattedMessage {...expiredUserModalMessages.viewPlans} />
            </Button>
            {shouldShowTopicsButton && (
              <Button
                as="a"
                href={libraryUrl}
                color="blue"
                variant="transparent"
                className="underline"
                rounded={true}
                data-testid={`${modalConfig.testId}-cta-view-topics`}
                onClick={handleViewTopicsClick}
              >
                <FormattedMessage {...expiredUserModalMessages.viewTopics} />
              </Button>
            )}
          </Flex>
        </Box>
      </Box>
    </Modal>
  );
};

export default ExpiredUserLockoutModal;
